import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MessageCircle, Map, Compass } from "lucide-react";

const STEPS = [
    {
        n: "01",
        icon: MessageCircle,
        title: "Share your side",
        body: "Each person privately records a voice note or types what happened, how they feel, and what matters most. No pressure, no audience.",
        accent: "Here’s what I’m hearing",
    },
    {
        n: "02",
        icon: Map,
        title: "See the shared map",
        body: "BetweenUs turns both sides into a calm, neutral summary — what you agree on, where you differ, and where the friction really lives.",
        accent: "Here’s where you overlap",
    },
    {
        n: "03",
        icon: Compass,
        title: "Find a way forward",
        body: "Get three fair, specific next-step ideas. Pick one together and turn it into a shared agreement with optional check-ins.",
        accent: "Here are a few paths forward",
    },
];

export default function HowItWorks() {
    const reduce = useReducedMotion();
    return (
        <section
            id="how-it-works"
            data-testid="how-it-works-section"
            className="relative py-20 sm:py-24 lg:py-28"
        >
            <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
                <div className="max-w-[720px]">
                    <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-[var(--brand-teal-2)]">
                        How it works
                    </p>
                    <h2
                        data-testid="how-it-works-heading"
                        className="mt-3 font-display text-[34px] sm:text-[42px] lg:text-[52px] leading-[1.05] tracking-[-0.02em] text-[var(--brand-ink)]"
                    >
                        Three quiet steps from{" "}
                        <span className="italic text-[var(--brand-navy)]">
                            friction
                        </span>{" "}
                        to{" "}
                        <span className="italic text-[var(--brand-teal-2)]">
                            forward
                        </span>
                        .
                    </h2>
                    <p className="mt-5 max-w-[60ch] text-[16px] leading-relaxed text-[var(--brand-muted-2)]">
                        Designed for asynchronous use. You don’t need to be
                        in the same room — or the same mood — to make real
                        progress.
                    </p>
                </div>

                <div className="mt-12 grid gap-5 sm:gap-6 md:grid-cols-3">
                    {STEPS.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <motion.article
                                key={s.n}
                                data-testid={`how-step-${i + 1}`}
                                initial={
                                    reduce
                                        ? false
                                        : { opacity: 0, y: 22 }
                                }
                                whileInView={
                                    reduce ? {} : { opacity: 1, y: 0 }
                                }
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{
                                    duration: 0.7,
                                    ease: [0.2, 0.8, 0.2, 1],
                                    delay: i * 0.08,
                                }}
                                className="group relative rounded-[22px] bg-white border border-brand p-6 sm:p-7 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-lift)] hover:-translate-y-[2px] transition-[transform,box-shadow,border-color] duration-300"
                            >
                                <div className="flex items-center justify-between">
                                    <span className="inline-flex items-center justify-center h-11 w-11 rounded-[14px] bg-[rgba(48,140,138,0.10)] border border-[rgba(48,140,138,0.22)] text-[var(--brand-teal-2)]">
                                        <Icon size={20} strokeWidth={1.8} />
                                    </span>
                                    <span className="font-display text-[20px] leading-none text-[var(--brand-border-strong)] tracking-[-0.02em]">
                                        {s.n}
                                    </span>
                                </div>
                                <h3 className="mt-5 font-display text-[22px] tracking-[-0.015em] text-[var(--brand-ink)]">
                                    {s.title}
                                </h3>
                                <p className="mt-2.5 text-[14.5px] leading-relaxed text-[var(--brand-muted-2)]">
                                    {s.body}
                                </p>
                                <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-[var(--brand-paper-2)] border border-brand px-3 py-1.5">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[var(--brand-gold)]" />
                                    <span className="text-[12px] font-medium italic text-[var(--brand-muted-2)]">
                                        “{s.accent}”
                                    </span>
                                </div>
                            </motion.article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
